/* ================================================================
   Structural Validator
   ----------------------------------------------------------------
   Runs a set of lint-style checks against the project model and the
   post-solve BOM summary. Produces a ValidationReport.

   Rules are intentionally shallow — enough to catch the "obvious
   mistakes" that tool2's 11-panel wizard flags. Heavier FEA-style
   analysis should live in a dedicated engine later.
   ================================================================ */

import type {
  ProjectModel, ValidationIssue, ValidationReport, BomSummary, SideId,
} from '../model/types';
import { resolveMaterial } from '../materials';

// --------------- Rule helpers ---------------

let _issueId = 1;
function issue(base: Partial<ValidationIssue> & {
  severity: ValidationIssue['severity'];
  title: string;
  detail: string;
  scope: ValidationIssue['scope'];
}): ValidationIssue {
  return {
    id: `v_${_issueId++}`,
    hint: undefined,
    sideId: undefined,
    layerIndex: undefined,
    ...base,
  };
}

// --------------- Rules ---------------

function ruleSpecMinThickness(project: ProjectModel): ValidationIssue[] {
  const spec = project.specification;
  if (!spec) return [];
  const out: ValidationIssue[] = [];

  for (const side of project.sides) {
    if (!side.enabled) continue;
    for (let li = 0; li < side.layers.length; li++) {
      const layer = side.layers[li];
      if (layer.style === 1 || layer.style === 0) {
        if (spec.minBoardThickness && layer.thickness < spec.minBoardThickness) {
          out.push(issue({
            severity: 'error',
            scope: 'layer',
            sideId: side.sideId,
            layerIndex: li,
            title: '板材厚度低于规范要求',
            detail: `${side.sideId} 第 ${li + 1} 层厚度 ${layer.thickness}mm，规范 ${spec.code} 要求 ≥ ${spec.minBoardThickness}mm`,
            hint: `将该层厚度调整至 ${spec.minBoardThickness}mm 或更厚`,
          }));
        }
      } else if (layer.style === 3) {
        if (spec.minFrameThickness && layer.thickness < spec.minFrameThickness) {
          out.push(issue({
            severity: 'error',
            scope: 'layer',
            sideId: side.sideId,
            layerIndex: li,
            title: '框架厚度低于规范要求',
            detail: `${side.sideId} 第 ${li + 1} 层框架厚度 ${layer.thickness}mm，规范 ${spec.code} 要求 ≥ ${spec.minFrameThickness}mm`,
            hint: `将该层厚度调整至 ${spec.minFrameThickness}mm 或更厚`,
          }));
        }
      }
    }
  }
  return out;
}

function rulePayloadVsBom(project: ProjectModel, bom: BomSummary): ValidationIssue[] {
  const out: ValidationIssue[] = [];
  const crateWeight = bom.totalWeightKg;
  const payload = project.payloadKg ?? 0;
  const gross = crateWeight + payload;

  if (crateWeight > payload * 0.8 && payload > 0) {
    out.push(issue({
      severity: 'warning',
      scope: 'project',
      title: '自重偏大',
      detail: `包装自重 ${crateWeight.toFixed(1)}kg 超过载重 ${payload}kg 的 80%，运输效率偏低`,
      hint: '考虑更薄的板材或结构优化',
    }));
  }

  if (gross > 1000) {
    out.push(issue({
      severity: 'info',
      scope: 'project',
      title: '总重 > 1000kg',
      detail: `总重（自重+载重）约 ${gross.toFixed(0)}kg，可能需要叉车配合`,
      hint: '确认底座承载和叉车孔间距',
    }));
  }
  return out;
}

function ruleStackLayers(project: ProjectModel): ValidationIssue[] {
  const spec = project.specification;
  const conditions = project.conditions;
  if (!spec?.recommendedStackLayers || !conditions) return [];

  const out: ValidationIssue[] = [];
  if (conditions.stackLayers > spec.recommendedStackLayers) {
    out.push(issue({
      severity: 'warning',
      scope: 'project',
      title: '堆码层数超出规范推荐',
      detail: `${spec.code} 推荐 ≤${spec.recommendedStackLayers} 层，当前 ${conditions.stackLayers} 层`,
      hint: '降低堆码层数或增加底座厚度',
    }));
  }
  return out;
}

function ruleEmptySides(project: ProjectModel): ValidationIssue[] {
  const out: ValidationIssue[] = [];
  for (const side of project.sides) {
    if (!side.enabled) continue;
    if (side.layers.length === 0) {
      out.push(issue({
        severity: 'error',
        scope: 'side',
        sideId: side.sideId,
        title: '启用面未配置任何层',
        detail: `${side.sideId} 已启用但没有层，将不会被求解`,
        hint: '在「工具 → 添加层」中为该面添加结构',
      }));
    }
  }
  return out;
}

function ruleDimensionsSanity(project: ProjectModel): ValidationIssue[] {
  const out: ValidationIssue[] = [];
  const { innerSize, outerSize } = project;
  const tooSmall = innerSize.length < 50 || innerSize.width < 50;

  if (tooSmall) {
    out.push(issue({
      severity: 'error',
      scope: 'project',
      title: '内尺寸过小',
      detail: `内长/宽 ${innerSize.length}×${innerSize.width} 低于 50mm`,
      hint: '请在 Ribbon「设置」中调整内尺寸',
    }));
  }
  if (outerSize.length < innerSize.length || outerSize.width < innerSize.width || outerSize.height < innerSize.height) {
    out.push(issue({
      severity: 'warning',
      scope: 'project',
      title: '外尺寸小于内尺寸',
      detail: '外尺寸未正确吸收层厚度，请检查',
      hint: '尝试触发一次层变更让系统重新计算',
    }));
  }
  return out;
}

// --------------- 承载力参考校验（简支梁弯曲 + 堆码压力） ---------------

/**
 * 底座抗弯校验：简支梁模型
 * 叉车两叉之间的底板跨度上承受货物重量，检查弯曲应力是否超过材料许用值。
 * σ = M / W，M = P*L/4（集中载荷简支梁），W = b*h²/6（矩形截面）
 * 安全系数取 3（木材离散性大）
 * ⚠️ 参考值，非精确计算
 */
function rulePedestalBending(project: ProjectModel): ValidationIssue[] {
  const out: ValidationIssue[] = [];
  const payload = project.payloadKg ?? 0;
  if (payload <= 0) return out;

  const pedestal = project.sides.find(s => s.sideId === 'pedestal');
  if (!pedestal?.enabled || pedestal.layers.length === 0) return out;

  // 找底座中的板条/整板层（承受弯矩的层）
  for (let li = 0; li < pedestal.layers.length; li++) {
    const layer = pedestal.layers[li];
    if (layer.style !== 0 && layer.style !== 1) continue; // 只校验板条和整板

    const mat = resolveMaterial(project, layer.materialKey);
    const bendingStrength = mat.bendingStrengthMPa ?? 40; // MPa
    const allowable = bendingStrength / 3; // 安全系数 3

    // 跨度取内尺寸长度（叉车叉间距近似）
    const span = project.innerSize.length; // mm
    const thickness = layer.thickness; // mm
    // 板条：取单根宽度 × 数量承载；整板：取面宽度
    const boardCount = layer.style === 0 ? (layer.styleSetting.count.board || 3) : 1;
    const totalWidth = layer.style === 0
      ? boardCount * (layer.blockBoard.boardWidth || 100)
      : project.innerSize.width;

    // 均布载荷下的最大弯矩 M = P*L/8（均布近似）
    const P = payload * 9.8; // N
    const M = (P * span) / 8; // N·mm
    const W = (totalWidth * thickness * thickness) / 6; // mm³
    const stress = M / W; // MPa

    const ratio = stress / allowable;

    if (ratio > 1) {
      out.push(issue({
        severity: 'error',
        scope: 'layer',
        sideId: 'pedestal',
        layerIndex: li,
        title: '底座承载力不足',
        detail: `载重 ${payload}kg，底板应力 ${stress.toFixed(1)}MPa 超过许用值 ${allowable.toFixed(1)}MPa（${mat.name} 安全系数3），应力比 ${ratio.toFixed(1)}`,
        hint: `增加板厚（当前 ${thickness}mm）、增加板条数量或换用更强材质`,
      }));
    } else if (ratio > 0.7) {
      out.push(issue({
        severity: 'warning',
        scope: 'layer',
        sideId: 'pedestal',
        layerIndex: li,
        title: '底座承载力偏紧',
        detail: `载重 ${payload}kg，应力比 ${(ratio * 100).toFixed(0)}%，余量不多（参考值）`,
        hint: '建议适当加厚底板或增加支撑',
      }));
    }
  }

  return out;
}

/**
 * 侧面堆码压力校验
 * 堆码时上方箱子的重量压在侧板上，检查压缩应力。
 * σ = F / A，A = 板厚 × 板长（截面积）
 * 木材顺纹抗压强度 ≈ 抗弯强度 × 1.2
 */
function ruleSideStackPressure(project: ProjectModel): ValidationIssue[] {
  const out: ValidationIssue[] = [];
  const payload = project.payloadKg ?? 0;
  const conditions = project.conditions;
  const stackLayers = conditions?.stackLayers ?? 1;
  if (stackLayers <= 1 || payload <= 0) return out;

  const totalWeightAbove = (payload + 30) * (stackLayers - 1); // 上方所有箱子重量（30kg箱体自重估算）
  const F = totalWeightAbove * 9.8; // N

  const wallSides: SideId[] = ['sideFront', 'sideBack', 'endLeft', 'endRight'];
  for (const sideId of wallSides) {
    const side = project.sides.find(s => s.sideId === sideId);
    if (!side?.enabled || side.layers.length === 0) continue;

    // 取最外层板
    const layer = side.layers[0];
    const mat = resolveMaterial(project, layer.materialKey);
    const compressStrength = (mat.bendingStrengthMPa ?? 40) * 1.2; // 顺纹抗压 ≈ 抗弯×1.2
    const allowable = compressStrength / 3;

    const boardLength = sideId === 'sideFront' || sideId === 'sideBack'
      ? project.outerSize.length : project.outerSize.width;
    const area = layer.thickness * boardLength; // mm²（承压截面）
    // 两面分担（前+后 或 左+右）
    const stress = F / (2 * area); // MPa

    const ratio = stress / allowable;

    if (ratio > 1) {
      out.push(issue({
        severity: 'error',
        scope: 'layer',
        sideId,
        layerIndex: 0,
        title: '堆码压力超限',
        detail: `堆码 ${stackLayers} 层，压力 ${stress.toFixed(1)}MPa 超过许用值 ${allowable.toFixed(1)}MPa，板厚 ${layer.thickness}mm 不够`,
        hint: '增加侧板厚度、改用田字框架结构或减少堆码层数',
      }));
    } else if (ratio > 0.7) {
      out.push(issue({
        severity: 'warning',
        scope: 'layer',
        sideId,
        layerIndex: 0,
        title: '堆码压力偏大',
        detail: `堆码 ${stackLayers} 层，应力比 ${(ratio * 100).toFixed(0)}%（参考值）`,
        hint: '考虑加厚侧板或改用田字结构',
      }));
    }
  }

  return out;
}

/**
 * 重量 vs 结构类型合理性检查
 */
function ruleWeightVsStructure(project: ProjectModel): ValidationIssue[] {
  const out: ValidationIssue[] = [];
  const payload = project.payloadKg ?? 0;
  if (payload <= 0) return out;

  // 重货用整板侧面 → 警告
  if (payload > 500) {
    const wallSides: SideId[] = ['sideFront', 'sideBack', 'endLeft', 'endRight'];
    for (const sideId of wallSides) {
      const side = project.sides.find(s => s.sideId === sideId);
      if (!side?.enabled) continue;
      for (let li = 0; li < side.layers.length; li++) {
        if (side.layers[li].style === 1 && side.layers[li].thickness < 20) {
          out.push(issue({
            severity: 'warning',
            scope: 'layer',
            sideId,
            layerIndex: li,
            title: '重货侧面结构偏弱',
            detail: `载重 ${payload}kg，${sideId} 使用 ${side.layers[li].thickness}mm 整板，建议改用田字框架或加厚`,
            hint: '500kg 以上建议侧面用田字(style=3)或板厚 ≥ 20mm',
          }));
        }
      }
    }
  }

  // 超重没有木方底座 → 错误
  if (payload > 300) {
    const pedestal = project.sides.find(s => s.sideId === 'pedestal');
    if (pedestal?.enabled) {
      const hasTimber = pedestal.layers.some(l => l.style === 4);
      if (!hasTimber) {
        out.push(issue({
          severity: payload > 1000 ? 'error' : 'warning',
          scope: 'side',
          sideId: 'pedestal',
          title: '重货底座缺少木方支撑',
          detail: `载重 ${payload}kg，底座没有木方(style=4)层，叉车/堆码时底部强度可能不足`,
          hint: '在底座添加木方层作为承重支撑',
        }));
      }
    }
  }

  return out;
}

// --------------- Entry point ---------------

export function validateProject(
  project: ProjectModel,
  bom?: BomSummary,
): ValidationReport {
  _issueId = 1;
  const issues: ValidationIssue[] = [
    ...ruleDimensionsSanity(project),
    ...ruleEmptySides(project),
    ...ruleSpecMinThickness(project),
    ...ruleStackLayers(project),
    ...(bom ? rulePayloadVsBom(project, bom) : []),
    ...rulePedestalBending(project),
    ...ruleSideStackPressure(project),
    ...ruleWeightVsStructure(project),
  ];

  let errorCount = 0, warningCount = 0, infoCount = 0;
  for (const i of issues) {
    if (i.severity === 'error') errorCount++;
    else if (i.severity === 'warning') warningCount++;
    else infoCount++;
  }

  return {
    issues,
    errorCount,
    warningCount,
    infoCount,
    passed: errorCount === 0,
  };
}
